// src/pages/SelectionOrders/components/edit/OrderBatchesTab.tsx
import React, { useMemo, useState, useRef, useEffect, useContext } from 'react';
import DefaultModal from '../../../../components/default/DefaultModal';
import ButtonCreate from '../../../../components/buttons/ButtonCreate';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import useSelectionOrderService from '../../../../app/services/hooks/useSelectionOrderService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { SelectionOrderBatchRow, SelectionOrderDestinationBatchRow } from '../../../../app/models/SelectionOrder';
import SelectTwo from '../../../../components/SelectTwo';
import { toast } from 'react-toastify';
import BatchStatusChange from '../BatchStatusChange';
import { AuthContext } from '../../../../contexts/AuthContext';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import { getTodayLocalDateString } from '../../../../helpers';

type SelectOpt = { id: number | string; name: string; unit_id?: number; project__name?: string | null };

type BatchOption = SelectOpt & {
  product_id?: number | null;
  product_name?: string | null;
  quantity?: number;
  available_quantity?: number | null;
  unit_name?: string | null;
  project_name?: string | null;
  product__name?: string;
  unit__name?: string;
};

type ProductOption = { id: number; name: string; unit_id?: number | null; unit_name?: string | null };

type Props = {
  orderId: number;
  pendingRows: SelectionOrderBatchRow[];
  destinationRows: SelectionOrderDestinationBatchRow[];
  onChanged: () => void;
  readOnly?: boolean;
};

const OrderBatchesTab = (props: Props) => {
  const {
    createOrderBatchOptions,
    storeOrderBatch,
    storeOrderBatchByBarcode,
    deleteOrderBatch,
    fetchingStoreOrderBatch,
    fetchingStoreOrderBatchByBarcode,
    createDestinationBatchOptions,
    storeDestinationBatch,
    voidDestinationBatch,
    fetchingStoreDestinationBatch,
    fetchingDeleteDestinationBatch,
  } = useSelectionOrderService();

  const { orderId, pendingRows, destinationRows, onChanged, readOnly = false } = props;

  const { auth } = useContext(AuthContext);
  const confirmAlert = useReactConfirmAlert();

  const [showCreate, setShowCreate] = useState(false);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [available, setAvailable] = useState<SelectOpt[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [barcodeValue, setBarcodeValue] = useState<string>('');
  const [selectedPendingLinkId, setSelectedPendingLinkId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const [showLoad, setShowLoad] = useState(false);
  const [loadDestBatchId, setLoadDestBatchId] = useState<number | null>(null);
  const [loadProductId, setLoadProductId] = useState<number | null>(null);
  const [loadQuantity, setLoadQuantity] = useState<string>('');
  const [loadRealDate, setLoadRealDate] = useState<string>(() => getTodayLocalDateString());
  const [loadBatches, setLoadBatches] = useState<BatchOption[]>([]);
  const [loadProducts, setLoadProducts] = useState<ProductOption[]>([]);
  const [loadErrors, setLoadErrors] = useState<any>(null);

  const [showCreatedByModal, setShowCreatedByModal] = useState(false);
  const [createdByInfo, setCreatedByInfo] = useState<{ name: string; created_at?: string } | null>(null);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidRecord, setVoidRecord] = useState<SelectionOrderDestinationBatchRow | null>(null);
  const [voidDate, setVoidDate] = useState<string>(() => getTodayLocalDateString());

  const selectedDestBatch = loadBatches.find(b => b.id === loadDestBatchId) ?? null;
  const destBatchQuantity = selectedDestBatch?.quantity ?? 0;
  const canChangeProduct = destBatchQuantity === 0;
  const selectedProduct = loadProducts.find(p => p.id === loadProductId) ?? null;
  const displayUnitName = selectedProduct?.unit_name ?? selectedDestBatch?.unit_name ?? '—';

  useEffect(() => {
    if (selectedDestBatch != null) {
      setLoadProductId(selectedDestBatch.product_id ?? null);
    } else {
      setLoadProductId(null);
    }
  }, [loadDestBatchId]);

  const reload = () => onChanged();

  const openCreate = () => {
    createOrderBatchOptions(orderId, {
      onSuccess: (resp: ServiceResponse) => {
        setAvailable(resp.data.available_batches || []);
        setShowCreate(true);
      }
    });
  };

  const openBarcodeModal = () => {
    setBarcodeValue('');
    setError(null);
    setShowBarcodeModal(true);
  };

  const doStore = () => {
    if (!selectedBatchId) { setError('Debe seleccionar un lote asignado'); return; }
    storeOrderBatch(orderId, { batch_id: selectedBatchId }, {
      onSuccess: () => { setShowCreate(false); setSelectedBatchId(null); reload(); },
      onError: (resp: ServiceResponse) => setError(resp.message)
    });
  };

  const doStoreByBarcode = () => {
    const barcode = barcodeValue.trim().toUpperCase();
    if (!barcode) {
      setError('Debe escanear o ingresar un código de barras');
      return;
    }

    storeOrderBatchByBarcode(orderId, { barcode }, {
      onSuccess: (resp: ServiceResponse) => {
        setShowBarcodeModal(false);
        setBarcodeValue('');
        setError(null);
        reload();
      },
      onError: (resp: ServiceResponse) => {
        setError(resp.message);
      }
    });
  };

  useEffect(() => {
    if (showBarcodeModal && barcodeInputRef.current) {
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
    }
  }, [showBarcodeModal]);

  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      doStoreByBarcode();
    }
  };

  const onClickLoad = () => {
    if (pendingRows.length === 0) {
      toast.info('Debe haber al menos un lote de origen en la orden para registrar inventario.');
      return;
    }
    createDestinationBatchOptions(orderId, {
      onSuccess: (resp: ServiceResponse) => {
        setLoadBatches(resp?.data?.batches || []);
        setLoadProducts(resp?.data?.products || []);
        setLoadDestBatchId(null);
        setLoadProductId(null);
        setLoadQuantity('');
        setLoadRealDate(getTodayLocalDateString());
        setLoadErrors(null);
        setShowLoad(true);
      },
      onError: (resp: ServiceResponse) => {
        toast.error(resp?.message || 'No se pudieron obtener los lotes de destino.');
      }
    }, selectedPendingLinkId ?? undefined);
  };

  const doStoreLoad = () => {
    if (!loadDestBatchId) { setLoadErrors({ destination_batch_id: ['Debe seleccionar un lote de destino'] }); return; }
    const qty = Number(loadQuantity);
    if (!loadQuantity || qty <= 0) { setLoadErrors({ quantity: ['La cantidad debe ser mayor a 0'] }); return; }

    const payload: { destination_batch_id: number; quantity: number; product_id?: number; real_date?: string } = {
      destination_batch_id: loadDestBatchId,
      quantity: qty,
    };
    if (destBatchQuantity === 0 && loadProductId != null) {
      payload.product_id = loadProductId;
    }
    if (loadRealDate) {
      payload.real_date = loadRealDate;
    }
    storeDestinationBatch(orderId, payload, {
        onSuccess: () => {
          toast.success('Lote de destino registrado con éxito.');
          setShowLoad(false);
          setLoadDestBatchId(null);
          setLoadQuantity('');
          setLoadErrors(null);
          reload();
        },
        onError: (resp: ServiceResponse) => {
          toast.error(resp?.message || 'Error al registrar el lote.');
        },
        onFieldError: (resp: ServiceResponse) => {
          setLoadErrors(resp.data);
        }
      }
    );
  };

  // Lotes tomados (RESERVED): se muestran con checkbox marcado
  const isTaken = (r: SelectionOrderBatchRow) => r.status === 'RESERVED';

  // Por defecto seleccionar el primer lote tomado para Inventariar
  useEffect(() => {
    if (selectedPendingLinkId == null && pendingRows.length > 0) {
      const firstTaken = pendingRows.find(isTaken);
      if (firstTaken) setSelectedPendingLinkId(firstTaken.id);
    }
  }, [pendingRows]);

  const PendingTable = useMemo(() => (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            {!readOnly && <th style={{ width: 36 }}></th>}
            <th>ID Lote</th>
            <th>Tomado por</th>
            <th style={{ minWidth: '120px' }}>Estado</th>
            {!readOnly && <th style={{ width: 80 }}>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {pendingRows.map((r) => (
            <tr
              key={r.id}
              className={selectedPendingLinkId === r.id ? 'table-active' : ''}
              onClick={() => !readOnly && r.status === 'RESERVED' && setSelectedPendingLinkId(r.id)}
              style={!readOnly && r.status === 'RESERVED' ? { cursor: 'pointer' } : undefined}
            >
              {!readOnly && (
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isTaken(r)}
                    readOnly
                    disabled
                    className="form-check-input"
                  />
                </td>
              )}
              <td>{r.batch_id}</td>
              <td>{r.created_by_name || '—'}</td>
              <td>
                {!readOnly ? (
                  <BatchStatusChange
                    linkId={r.id}
                    currentStatus={r.status}
                    currentStatusDisplay={r.status_display}
                    callbackSuccess={reload}
                  />
                ) : (
                  <span>{r.status_display || r.status || '-'}</span>
                )}
              </td>
              {!readOnly && (
                <td>
                  <ButtonTableAction
                    title="Quitar"
                    classIcon="mdi mdi-delete"
                    colorIcon="text-danger"
                    callbackFunction={() =>
                      deleteOrderBatch(r.id, {
                        onSuccess: reload,
                        onError: (resp) => {
                          confirmAlert.errorAlert({
                            title: 'No se puede quitar el lote',
                            message:
                              resp?.message ||
                              'No se puede eliminar este lote de origen porque tiene movimientos de inventario realizados en esta orden.',
                          });
                          reload();
                        }
                      })
                    }
                  />
                </td>
              )}
            </tr>
          ))}
          {pendingRows.length === 0 && (
            <tr>
              <td colSpan={readOnly ? 3 : 4} className="text-center text-muted">
                Sin lotes pendientes
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  ), [pendingRows, selectedPendingLinkId, readOnly]);

  const DestinationTable = useMemo(() => (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            <th>Lote destino</th>
            <th>Producto</th>
            <th>Cantidad</th>
            <th>Unidad</th>
            {!readOnly && <th style={{ width: 100 }}>Acciones</th>}
          </tr>
        </thead>
        <tbody>
          {destinationRows.map((r) => (
            <tr key={r.id}>
              <td>#{r.destination_batch_id}</td>
              <td>{r.product_name ?? '—'}</td>
              <td>{r.quantity}</td>
              <td>{r.unit_name ?? '—'}</td>
              {!readOnly && (
                <td>
                  <ButtonTableAction
                    title="Ver creado por"
                    classIcon="mdi mdi-eye"
                    colorIcon="text-info"
                    callbackFunction={() => {
                      setCreatedByInfo({
                        name: r.created_by_name ?? '—',
                        created_at: r.created_at,
                      });
                      setShowCreatedByModal(true);
                    }}
                  />
                  <ButtonTableAction
                    title="Anular"
                    classIcon="mdi mdi-close-circle"
                    colorIcon="text-warning"
                    callbackFunction={() => {
                      setVoidRecord(r);
                      setVoidDate(getTodayLocalDateString());
                      setShowVoidModal(true);
                    }}
                  />
                </td>
              )}
            </tr>
          ))}
          {destinationRows.length === 0 && (
            <tr>
              <td colSpan={readOnly ? 4 : 5} className="text-center text-muted">
                Sin lotes de destino registrados
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  ), [destinationRows, readOnly]);

  return (
    <>
      {!readOnly && (
        <div className="d-flex justify-content-start mb-2 gap-2">
          <button
            className="btn btn-success btn-sm"
            onClick={openBarcodeModal}
          >
            <i className="mdi mdi-barcode-scan me-1"></i>
            Tomar Lote
          </button>
          <ButtonCreate title="Agregar lote" callbackCreate={openCreate} />
        </div>
      )}

      <div className="row g-3 align-items-start">
        <div className="col-12 col-lg-5">
          <h6 className="mb-2">Lotes de origen</h6>
          {PendingTable}
        </div>

        <div className="col-12 col-lg-2 d-flex flex-column align-items-center justify-content-center">
          {!readOnly && (
            <button
              className="btn btn-primary mb-2"
              onClick={onClickLoad}
              disabled={pendingRows.length === 0}
            >
              Inventariar &raquo;
            </button>
          )}
        </div>

        <div className="col-12 col-lg-5">
          <h6 className="mb-2">Lotes de destino</h6>
          {DestinationTable}
        </div>
      </div>

      {!readOnly && showCreate && (
        <DefaultModal
          show={showCreate}
          handleClose={() => setShowCreate(false)}
          title="Agregar lote"
          showFooter={false}
          backdrop={true}
        >
          <div className="mb-3">
            <label className="col-form-label">Lote</label>
            <SelectTwo
              name="batch_id"
              id="batch_id"
              inputValue={selectedBatchId}
              options={(available || []).map(a => ({
                value: a.id,
                label: a.project__name ? `#${a.id} | ${a.project__name}` : `#${a.id}`,
              }))}
              onChange={(v: any) => { setSelectedBatchId(v?.value ?? null); setError(null); }}
              placeholder="Seleccione lote"
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>
          <div className="d-flex justify-content-end">
            <button className="btn btn-secondary me-2" onClick={() => setShowCreate(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={doStore} disabled={!!fetchingStoreOrderBatch}>Agregar</button>
          </div>
        </DefaultModal>
      )}

      {!readOnly && showBarcodeModal && (
        <DefaultModal
          show={showBarcodeModal}
          handleClose={() => setShowBarcodeModal(false)}
          title="Escanear código de barras del lote"
          showFooter={false}
          backdrop={true}
        >
          <div className="alert alert-info" role="alert">
            <i className="mdi mdi-information me-2"></i>
            Escanee el código de barras del lote
          </div>

          <div className="mb-3">
            <label className="col-form-label">
              <i className="mdi mdi-barcode-scan me-2"></i>
              Código de barras
            </label>
            <input
              ref={barcodeInputRef}
              type="text"
              className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
              value={barcodeValue}
              onChange={(e) => {
                setBarcodeValue(e.target.value);
                setError(null);
              }}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="BATCH-00000000"
              autoComplete="off"
              style={{
                fontSize: '1.2rem',
                letterSpacing: '0.1em',
                fontFamily: 'monospace'
              }}
            />
            <small className="form-text text-muted">
              El lote se agregará automáticamente al presionar Enter o hacer clic en Agregar
            </small>
            {error && <div className="invalid-feedback d-block">{error}</div>}
          </div>

          <div className="d-flex justify-content-end">
            <button
              className="btn btn-secondary me-2"
              onClick={() => setShowBarcodeModal(false)}
              disabled={!!fetchingStoreOrderBatchByBarcode}
            >
              Cancelar
            </button>
            <button
              className="btn btn-success"
              onClick={doStoreByBarcode}
              disabled={!!fetchingStoreOrderBatchByBarcode || !barcodeValue.trim()}
            >
              <i className="mdi mdi-check me-1"></i>
              {fetchingStoreOrderBatchByBarcode ? 'Agregando...' : 'Agregar'}
            </button>
          </div>
        </DefaultModal>
      )}

      {!readOnly && showLoad && (
        <DefaultModal
          show={showLoad}
          handleClose={() => setShowLoad(false)}
          title="Registrar inventario"
          showFooter={false}
          backdrop={true}
        >
          <div>
            <div className="mb-3">
              <label className="col-form-label fw-semibold">Fecha real</label>
              <input
                type="date"
                className={`form-control ${loadErrors?.real_date ? 'is-invalid' : ''}`}
                value={loadRealDate}
                onChange={e => { setLoadRealDate(e.target.value); setLoadErrors(null); }}
              />
              {loadErrors?.real_date && (
                <div className="invalid-feedback d-block">{loadErrors.real_date[0]}</div>
              )}
              <small className="text-muted">Fecha de contabilización del movimiento.</small>
            </div>

            <div className="mb-3">
              <label className="col-form-label fw-semibold">Lote de destino</label>
              <SelectTwo
                name="destination_batch_id"
                id="destination_batch_id"
                inputValue={loadDestBatchId}
                options={loadBatches.map(b => {
                  const parts: string[] = [`#${b.id}`];
                  if (b.available_quantity != null) parts.push(`Disp: ${b.available_quantity}`);
                  if (b.project_name) parts.push(b.project_name);
                  return { value: b.id, label: parts.join(' | ') };
                })}
                onChange={(v: any) => { setLoadDestBatchId(v?.value ?? null); setLoadErrors(null); }}
                placeholder="Seleccione lote de destino"
              />
              {loadErrors?.destination_batch_id && (
                <div className="invalid-feedback d-block">{loadErrors.destination_batch_id[0]}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="col-form-label fw-semibold">Productos</label>
              <SelectTwo
                name="load_product_id"
                id="load_product_id"
                inputValue={loadProductId}
                options={loadProducts.map(p => ({
                  value: p.id,
                  label: p.unit_name ? `${p.name} (${p.unit_name})` : p.name,
                }))}
                onChange={(v: any) => { setLoadProductId(v?.value ?? null); setLoadErrors(null); }}
                placeholder="Seleccione producto"
                disabled={!canChangeProduct}
              />
              {!canChangeProduct && selectedDestBatch && (
                <small className="text-muted">El producto no se puede cambiar porque el lote tiene cantidad mayor a 0.</small>
              )}
            </div>

            {selectedDestBatch && (
              <div className="alert alert-info py-2 mb-3">
                <strong>Unidad:</strong> {displayUnitName}
                <br /><strong>Producto {canChangeProduct ? 'seleccionado' : 'actual del lote'}:</strong> {selectedProduct?.name ?? selectedDestBatch.product_name ?? '—'}
                {selectedDestBatch.available_quantity != null && <><br /><strong>Cantidad disponible:</strong> {selectedDestBatch.available_quantity}</>}
                {selectedDestBatch.project_name && <><br /><strong>Proyecto:</strong> {selectedDestBatch.project_name}</>}
              </div>
            )}

            <div className="mb-3">
              <label className="col-form-label fw-semibold">Cantidad</label>
              <div className="input-group">
                <input
                  type="number"
                  className={`form-control ${loadErrors?.quantity ? 'is-invalid' : ''}`}
                  value={loadQuantity}
                  onChange={e => { setLoadQuantity(e.target.value); setLoadErrors(null); }}
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                />
                <span className="input-group-text">
                  {displayUnitName}
                </span>
                {loadErrors?.quantity && (
                  <div className="invalid-feedback d-block">{loadErrors.quantity[0]}</div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-secondary me-2" onClick={() => setShowLoad(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={doStoreLoad} disabled={!!fetchingStoreDestinationBatch}>
                {fetchingStoreDestinationBatch ? 'Creando...' : 'Crear inventario'}
              </button>
            </div>
          </div>
        </DefaultModal>
      )}

      {showVoidModal && voidRecord && (
        <DefaultModal
          show={showVoidModal}
          handleClose={() => { setShowVoidModal(false); setVoidRecord(null); }}
          title="¿Anular este inventario?"
          showFooter={false}
          backdrop={true}
        >
          <p className="mb-3">
            Se creará el movimiento inverso (salida) en el lote #{voidRecord.destination_batch_id} por {voidRecord.quantity} {voidRecord.unit_name ?? ''}. Esta acción solo es posible si la orden no está finalizada.
          </p>
          <div className="mb-3">
            <label className="col-form-label fw-semibold">Fecha real</label>
            <input
              type="date"
              className="form-control"
              value={voidDate}
              onChange={e => setVoidDate(e.target.value)}
            />
            <small className="text-muted">Fecha de contabilización de la anulación.</small>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => { setShowVoidModal(false); setVoidRecord(null); }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-success"
              disabled={!!fetchingDeleteDestinationBatch}
              onClick={() => {
                voidDestinationBatch(voidRecord.id, {
                  onSuccess: (resp: ServiceResponse) => {
                    toast.success(resp?.message ?? 'Movimiento anulado correctamente.');
                    setShowVoidModal(false);
                    setVoidRecord(null);
                    reload();
                  },
                  onError: (resp: ServiceResponse) => {
                    confirmAlert.errorAlert({
                      title: 'No se puede anular',
                      message: resp?.message ?? 'No se pudo anular el movimiento. Verifique que la orden no esté finalizada.',
                    });
                    setShowVoidModal(false);
                    setVoidRecord(null);
                    reload();
                  },
                }, { real_date: voidDate });
              }}
            >
              {fetchingDeleteDestinationBatch ? 'Anulando...' : 'Sí, anular'}
            </button>
          </div>
        </DefaultModal>
      )}

      {showCreatedByModal && (
        <DefaultModal
          show={showCreatedByModal}
          handleClose={() => setShowCreatedByModal(false)}
          title="Creado por"
          showFooter={false}
          backdrop={true}
        >
          <div className="py-2">
            <p className="mb-0">
              <strong>{createdByInfo?.name ?? '—'}</strong>
            </p>
            {createdByInfo?.created_at && (
              <small className="text-muted">Fecha: {createdByInfo.created_at}</small>
            )}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <button className="btn btn-secondary" onClick={() => setShowCreatedByModal(false)}>
              Cerrar
            </button>
          </div>
        </DefaultModal>
      )}
    </>
  );
};

export default OrderBatchesTab;